import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Spinner
} from '@heroui/react';
import { DocumentPlusIcon, CalendarIcon } from '@heroicons/react/24/outline';

const QuickDraftModal = ({ isOpen, onClose, onGenerateDraft, isDrafting }) => {
  const [formData, setFormData] = useState({
    suspects: '',
    victims: '',
    location: '',
    date: '',
    time: '',
    offense: '',
    summary: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const setTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}-${month}-${day}`;
    handleInputChange('date', todayString);
  };

  const handleGenerate = async () => {
    // Check if at least one field has content
    const hasContent = Object.values(formData).some(value => value.trim());
    
    if (hasContent) {
      await onGenerateDraft(formData);
      setFormData({
        suspects: '',
        victims: '',
        location: '',
        date: '',
        time: '',
        offense: '',
        summary: ''
      });
      onClose();
    }
  };

  const hasContent = Object.values(formData).some(value => value.trim());

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose} size="2xl">
      <ModalContent>
        {(onCloseModal) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <DocumentPlusIcon className="h-6 w-6 text-success" />
                Quick Draft - Police Report
              </div>
            </ModalHeader>
            <ModalBody>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Fill in any relevant details to generate a police report draft. All fields are optional.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Suspect(s)"
                  placeholder="Name(s) of suspect(s)"
                  variant="bordered"
                  value={formData.suspects}
                  onChange={(e) => handleInputChange('suspects', e.target.value)}
                />
                <Input
                  label="Victim(s)"
                  placeholder="Name(s) of victim(s)"
                  variant="bordered"
                  value={formData.victims}
                  onChange={(e) => handleInputChange('victims', e.target.value)}
                />
                <Input
                  label="Location"
                  placeholder="Location of incident"
                  variant="bordered"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                />
                
                <div className="flex gap-2">
                  <Input
                    label="Date"
                    placeholder="Date of incident"
                    type="date"
                    variant="bordered"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    color="default"
                    variant="bordered"
                    size="sm"
                    onPress={setTodayDate}
                    className="mt-6"
                    startContent={<CalendarIcon className="h-4 w-4" />}
                  >
                    Today
                  </Button>
                </div>
                
                <Input
                  label="Time"
                  placeholder="Time of incident"
                  type="time"
                  variant="bordered"
                  value={formData.time}
                  onChange={(e) => handleInputChange('time', e.target.value)}
                />
                
                <Input
                  label="Offense"
                  placeholder="Type of offense"
                  variant="bordered"
                  value={formData.offense}
                  onChange={(e) => handleInputChange('offense', e.target.value)}
                  className="md:col-span-2"
                />
              </div>
              
              <Textarea
                label="Summary"
                placeholder="Brief summary of the incident"
                variant="bordered"
                value={formData.summary}
                onChange={(e) => handleInputChange('summary', e.target.value)}
                minRows={3}
                className="mt-4"
              />
              
              {isDrafting && (
                <div className="flex items-center justify-center mt-4">
                  <Spinner size="sm" color="success" />
                  <span className="ml-2 text-gray-600 dark:text-gray-400">Generating draft...</span>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="default" variant="light" onPress={onCloseModal} isDisabled={isDrafting}>
                Cancel
              </Button>
              <Button color="success" onPress={handleGenerate} isDisabled={!hasContent || isDrafting}>
                Generate Draft
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default QuickDraftModal;
